import React, {useEffect} from "react";
import ServiceCollection from "src/services/ServiceCollection";
import {
    InlineEditingHandlerAttributes, mappingUpdateMessage,
    NO_DATA_MESSAGE, NO_MATCH_MESSAGE
} from "src/components/ic-account-mapping/constants";
import {
    PropertyFilterOperator,
    PropertyFilterOperatorExtended,
    PropertyFilterProperty,
    useCollection
} from "@amzn/awsui-collection-hooks";
import EmptyTableView from "src/components/ic-account-mapping/shared/EmptyTableView";
import {formatDateTime} from "src/components/ic-account-mapping/Utils";
import {Box, CollectionPreferencesProps, Select, StatusIndicator} from "@amzn/awsui-components-react";
import {DateTimeForm} from "src/components/ic-account-mapping/shared/DateTimeForm";
import OverrideWarningModal from "src/components/ic-account-mapping/shared/OverrideWarningModal";
import BaseTableView from "src/components/ic-account-mapping/shared/BaseTableView";
import {TableProps} from "@amzn/awsui-components-react/polaris/table";
import {
    AccountMappingRecord,
    AccountMappingStatus,
    AccountMappingType,
    AccountType
} from "src/models/reports/GetAccountMapping";
import Spinner from "@amzn/awsui-components-react/polaris/spinner";
import {UpdateAccountMappingRequest} from "src/models/reports/UpdateAccountMapping";
import useLocalStorage from "src/utils/useLocalStorage";

const ColumnIdToColumnNameMapping = {
    accountId: "Account ID",
    accountName: "Account Name",
    parentAccountId: "Parent ID",
    parentAccountName: "Parent Name",
    mappingStatus: "Status",
    defaultMapping: "Default Mapping",
    exceptionMapping: "Exception Mapping",
    updatedBy: "Exception Updated By",
    updatedAt: "Exception Updated On"
}

function getEditConfig(columnId: string): TableProps.EditConfig<any> | undefined {
    switch (columnId) {
        case ColumnIdToColumnNameMapping.exceptionMapping:
            return {
                editingCell: (rowItem, {currentValue, setValue}) => {
                    const value = currentValue ?? rowItem.exceptionMapping;
                    return (
                        <Select
                            autoFocus={true}
                            expandToViewport={true}
                            selectedOption={
                                Object.values(AccountType).map(value => ({
                                    label: value,
                                    value: value
                                })).find(
                                    option => option.value === value
                                ) ?? null
                            }
                            onChange={event => {
                                setValue(
                                    event.detail.selectedOption.value ??
                                    rowItem.exceptionMapping
                                );
                            }}
                            options={Object.values(AccountType).map(value => ({
                                label: value,
                                value: value
                            }))}
                        />
                    );
                }
            };
        default:
            return undefined;
    }
}

function prepareColumnDefinition(): TableProps.ColumnDefinition<any>[] {
    const columnDefinitions: TableProps.ColumnDefinition<any>[] = [];
    Object.entries(ColumnIdToColumnNameMapping).forEach(([columnId, columnName]: [string, string]) => {
        columnDefinitions.push({
            id: columnId,
            header: columnName,
            cell: values => {
                if (columnName === ColumnIdToColumnNameMapping.mappingStatus) {
                    if (values[columnId] === AccountMappingStatus.MAPPED) {
                        return <StatusIndicator type="success">{AccountMappingStatus.MAPPED}</StatusIndicator>
                    } else {
                        return <StatusIndicator type="pending">{AccountMappingStatus.UNMAPPED}</StatusIndicator>
                    }
                } else if (columnName === ColumnIdToColumnNameMapping.updatedAt) {
                    return values[columnId] ? values[columnId].toISOString() : '-';
                } else {
                    return values[columnId] ?? '-';
                }
            },
            isRowHeader: true,
            sortingField: columnId,
            editConfig: getEditConfig(columnName),
        });
    });

    return columnDefinitions;
}

// These custom operators are based on UX requirements
function getFilteringOperators(columnName: string): (PropertyFilterOperator | PropertyFilterOperatorExtended<any>)[] {
    switch (columnName) {
        case ColumnIdToColumnNameMapping.accountName:
        case ColumnIdToColumnNameMapping.accountId:
        case ColumnIdToColumnNameMapping.parentAccountName:
        case ColumnIdToColumnNameMapping.parentAccountId:
            return ["=", ":"];
        case ColumnIdToColumnNameMapping.mappingStatus:
        case ColumnIdToColumnNameMapping.updatedBy:
            return ["="];
        case ColumnIdToColumnNameMapping.defaultMapping:
        case ColumnIdToColumnNameMapping.exceptionMapping:
            return ["=", "!="];
        case ColumnIdToColumnNameMapping.updatedAt:
            return [">=", "<="].map((operator) => {
                return {
                    operator: operator,
                    match: "datetime",
                    form: DateTimeForm,
                    format: formatDateTime,
                }
            })
        default:
            return ["=", ":", "!="];
    }
}

export default function ManageExceptionsCLIView(props: { services: ServiceCollection, selectedCli: string }) {
    const [refresh, setRefresh] = React.useState(true);
    const [getAccountMappingResult, getAccountMappingLoading, getAccountMappingError] = props.services.reportsService.getAccountMapping(AccountMappingType.CLI, props.selectedCli, refresh);
    const [accountMappingData, setAccountMappingData] = React.useState<AccountMappingRecord[]>([]);
    const [preferences, setPreferences] = React.useState<CollectionPreferencesProps.Preferences>({
        pageSize: 10,
    });
    const [displayOverrideWarningModal, setDisplayOverrideWarningModal] = React.useState(false);
    const [inlineEditingHandlerAttributes, setInlineEditingHandlerAttributes] = React.useState<InlineEditingHandlerAttributes>({
        item: null,
        column: null,
        newValue: null
    });
    const [updateAccountMappingRequest, setUpdateAccountMappingRequest] = React.useState<UpdateAccountMappingRequest | null>(null);
    const [updateAccountMappingResponse, updateAccountMappingLoading, updateAccountMappingError] = props.services.reportsService.updateAccountMapping(updateAccountMappingRequest);

    useEffect(() => {
        if (getAccountMappingResult) {
            setAccountMappingData(getAccountMappingResult.accountMappings.map((accountMapping: AccountMappingRecord) => {
                return {
                    ...accountMapping,
                    updatedAt: accountMapping.updatedAt ? new Date(accountMapping.updatedAt) : ''
                }
            }))
        }
    }, [getAccountMappingResult]);

    useEffect(() => {
        if (getAccountMappingError || updateAccountMappingError) {
            props.services.messageService.showErrorAutoDismissBanner(getAccountMappingError || updateAccountMappingError);
        }
    }, [getAccountMappingError, updateAccountMappingError]);

    useEffect(() => {
        if (inlineEditingHandlerAttributes && inlineEditingHandlerAttributes.item) {
            if (inlineEditingHandlerAttributes.item.exceptionMapping && inlineEditingHandlerAttributes.item.exceptionMapping !== "-") {
                setDisplayOverrideWarningModal(true);
            } else {
                handleExceptionMappingUpdate();
            }
        }
    }, [inlineEditingHandlerAttributes]);

    useEffect(() => {
        if (updateAccountMappingResponse && updateAccountMappingResponse.response && updateAccountMappingResponse.response === 'Success') {
            props.services.messageService.showSuccessAutoDismissBanner(mappingUpdateMessage('exception'));
            setRefresh(!refresh);
        }
    }, [updateAccountMappingResponse]);

    const useCollectionResult = useCollection(
        accountMappingData,
        {
            propertyFiltering: {
                empty: <EmptyTableView message={NO_DATA_MESSAGE}/>,
                noMatch: <EmptyTableView message={NO_MATCH_MESSAGE}/>,
                filteringProperties: (Object.entries(ColumnIdToColumnNameMapping).map(([columnId, columnName]: [string, string]): PropertyFilterProperty => ({
                    key: columnId,
                    propertyLabel: columnName,
                    groupValuesLabel: `${columnName} Values`,
                    operators: getFilteringOperators(columnName),
                    // Defining defaultOperator is required to remove "=" from operators list for defaultUpdatedOn column
                    defaultOperator: columnName === ColumnIdToColumnNameMapping.updatedAt ? "<=" : "="
                }))),
                defaultQuery: {
                    tokens: [],
                    operation: "or"
                }
            },
            pagination: {
                pageSize: preferences.pageSize
            },
            sorting: {}
        }
    );

    const handleExceptionMappingUpdate = () => {
        setUpdateAccountMappingRequest({
            accountId: inlineEditingHandlerAttributes.item.accountId,
            mappingType: AccountMappingType.CLI,
            accountType: inlineEditingHandlerAttributes.newValue as AccountType,
            updatedBy: useLocalStorage.getUsernameFromLocalStorage(),
            exceptionMappingFor: props.selectedCli
        });
        setDisplayOverrideWarningModal(false);
    }

    return (
        <>
            <OverrideWarningModal
                visible={displayOverrideWarningModal}
                onCancel={() => {
                    setDisplayOverrideWarningModal(false);
                }}
                onConfirm={handleExceptionMappingUpdate}
                alertMessage={"There is existing exception mapping for the selected account. Proceed to override the current\n" +
                    "configuration or click Cancel."}
            />

            {
                getAccountMappingLoading || updateAccountMappingLoading ? (
                        <Box textAlign="center">
                            <Spinner size="large"/>
                        </Box>
                    ) :
                    <BaseTableView header={props.selectedCli} columnDefinitions={prepareColumnDefinition()}
                                   preferences={preferences} setPreferences={setPreferences}
                                   useCollectionResult={useCollectionResult}
                                   setInlineEditingHandlerAttributes={setInlineEditingHandlerAttributes}
                        // TODO: Replace description with workbook that will come in API call
                        //            description={"CWB00149 - Last Mile Delivery Services"}

                    />
            }
        </>
    )
}